import axios from 'axios';
import { saveRedirectPath } from '../utils/authUtils';
import { isRateLimitError } from '../utils/errorUtils';

// api base url
const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  withCredentials: false
});

// custom error handler for better feedback
const handleApiError = (error) => {
  // Check for 401 Unauthorized errors
  if (error.response && error.response.status === 401) {
    console.log('Authentication error: Token invalid or expired');
    
    // Clear stored authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    
    // Redirect to login page if not already on login or register page
    const currentPath = window.location.pathname;
    if (!['/login', '/register', '/privacy', '/dmca', '/welcome'].includes(currentPath)) {
      // Save current location to redirect back after login (if not root)
      if (currentPath !== '/') {
        console.log('API error handler: Saving redirect path:', currentPath);
        saveRedirectPath(currentPath);
        
        // Redirect to login page
        window.location.href = '/login';
      } else {
        // For root path, go to welcome page
        window.location.href = '/welcome';
      }
    }
  }

  // check for rate limiting using the shared utility
  if (isRateLimitError(error)) {
    // create error with friendly message
    const customError = new Error("You've reached the rate limit. Please wait a moment before trying again.");
    customError.isRateLimit = true;
    customError.originalError = error;
    return Promise.reject(customError);
  }
  
  // pass through other errors
  return Promise.reject(error);
};

// Setup a response interceptor to handle authentication errors globally
api.interceptors.response.use(
  response => response,
  error => handleApiError(error)
);

// add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Popularity API endpoints
export const PopularityAPI = {
  getTrending: (limit = 10, period = 'week') => {
    return api.get(`/popularity/trending`, { 
      params: { limit, period }
    });
  },
  
  getPopularMovies: (limit = 10, period = 'week') => {
    return api.get(`/popularity/movies`, { 
      params: { limit, period }
    });
  },
  
  getPopularShows: (limit = 10, period = 'week') => {
    return api.get(`/popularity/shows`, { 
      params: { limit, period }
    });
  },
  
  trackContentView: (contentType, contentId) => {
    return api.post(`/popularity/track/${contentType}/${contentId}`);
  }
};

// movies api endpoints
export const MoviesAPI = {
  getAll: (params = {}) => {
    return api.get('/movies', { params });
  },
  getFeatured: () => {
    return api.get('/movies/featured');
  },
  getById: (id) => {
    return api.get(`/movies/${id}`);
  },
  updateWatchStatus: (id, data) => {
    // get user id from storage
    const userData = localStorage.getItem('user');
    let userId = null;
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userId = user._id;
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
    
    // Ensure data types are correct
    const progress = Number(data.progress); // Ensure progress is a number
    const completed = Boolean(data.completed); // Ensure completed is a boolean
    userId = userId || data.userId;
    
    console.log(`Sending watch status for movie ${id}:`, { progress, completed, userId });
    
    // Add retry logic to make progress tracking more reliable
    const maxRetries = 3;
    let retryCount = 0;
    
    const attemptUpdate = async () => {
      try {
        // Build URL with query parameters to ensure they're properly encoded
        const url = `/movies/${id}/watch?progress=${progress}&completed=${completed}`;
        console.log(`Making request to: ${url}`);
        
        // Use query parameters instead of request body
        const response = await api.post(url, { userId });
        
        console.log(`Successfully updated movie ${id} progress:`, response.data);
        return response;
      } catch (err) {
        console.error(`Error updating movie ${id} progress:`, err);
        console.error('Request failed with:', err.response?.data || err.message);
        
        if (retryCount < maxRetries) {
          retryCount++;
          // Exponential backoff: 1s, 2s, 4s, etc.
          const delay = Math.pow(2, retryCount) * 1000;
          console.log(`Retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptUpdate(); // Retry after delay
        }
        throw err; // If all retries fail, throw the error
      }
    };
    
    return attemptUpdate();
  }
};

// shows api endpoints
export const ShowsAPI = {
  getAll: (params = {}) => {
    return api.get('/shows', { params });
  },
  // get all episodes in a season
  getAllSeasonEpisodes: (showId, seasonId) => {
    return api.get(`/shows/${showId}/season/${seasonId}/all-episodes`);
  },
  
  getFeatured: () => {
    return api.get('/shows/featured');
  },
  getById: (id) => {
    return api.get(`/shows/${id}`);
  },
  getEpisodeById: (id) => {
    return api.get(`/shows/episode/${id}`);
  },
  // get paginated episode data
  getSeasonEpisodes: (showId, seasonId, page = 1, limit = 10) => {
    return api.get(`/shows/${showId}/season/${seasonId}/episodes`, { 
      params: { page, limit } 
    });
  },
  updateEpisodeWatchStatus: (id, data) => {
    const userData = localStorage.getItem('user');
    let userId = null;
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userId = user._id;
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
    
    // Include user id in request and ensure data types are correct
    const progress = Number(data.progress); // Ensure progress is a number
    const completed = Boolean(data.completed); // Ensure completed is a boolean
    userId = userId || data.userId;
    
    console.log(`Sending watch status for episode ${id}:`, { progress, completed, userId });
    
    // Add retry logic to make progress tracking more reliable
    const maxRetries = 3;
    let retryCount = 0;
    
    const attemptUpdate = async () => {
      try {
        // Use query parameters instead of request body
        const response = await api.post(
          `/shows/episode/${id}/watch?progress=${progress}&completed=${completed}`,
          { userId }
        );
        console.log(`Successfully updated episode ${id} progress:`, response.data);
        return response;
      } catch (err) {
        console.error(`Error updating episode ${id} progress:`, err);
        if (retryCount < maxRetries) {
          retryCount++;
          // Exponential backoff: 1s, 2s, 4s, etc.
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptUpdate(); // Retry after delay
        }
        throw err; // If all retries fail, throw the error
      }
    };
    
    return attemptUpdate();
  }
};

// Notifications API endpoints
export const NotificationsAPI = {
  getNotifications: (params = {}, cancelToken = null) => {
    // Add a cache-busting parameter to prevent browser caching
    const cacheBuster = { _t: new Date().getTime() };
    return api.get('/notifications', { 
      params: { ...params, ...cacheBuster },
      // Shorter timeout for notifications to prevent hanging UI
      timeout: 8000,
      cancelToken: cancelToken
    });
  },
  
  getUnreadCount: (cancelToken = null) => {
    // Add a cache-busting parameter to prevent browser caching
    const cacheBuster = { _t: new Date().getTime() };
    return api.get('/notifications/unread-count', {
      params: cacheBuster,
      // Use a shorter timeout for this simple request
      timeout: 5000,
      cancelToken: cancelToken
    });
  },
  
  markAsRead: (notificationId) => {
    return api.put(`/notifications/${notificationId}/read`, {}, {
      // Use a shorter timeout for this simple request
      timeout: 5000
    });
  },
  
  markAllAsRead: () => {
    return api.put('/notifications/read-all', {}, {
      // Use a shorter timeout for this simple request
      timeout: 5000
    });
  },
  
  deleteNotification: (notificationId) => {
    return api.delete(`/notifications/${notificationId}`, {
      // Use a shorter timeout for this simple request
      timeout: 5000
    });
  },
  
  deleteAllNotifications: () => {
    return api.delete('/notifications', {
      // Use a shorter timeout for this simple request
      timeout: 5000
    });
  }
};

// Episode Suggestions API endpoints
export const SuggestionsAPI = {
  createEpisodeSuggestion: (episodeId, data) => {
    return api.post(`/suggestions/episode/${episodeId}`, data);
  },
  
  getUserSuggestions: (params = {}) => {
    return api.get('/suggestions/user', { params });
  },
  
  getEpisodeSuggestions: (episodeId, params = {}) => {
    return api.get(`/suggestions/episode/${episodeId}`, { params });
  }
};

// user account api endpoints
export const UserAPI = {
  getWatchHistory: () => {
    return api.get('/user/history');
  },
  getContinueWatching: () => {
    return api.get('/user/continue-watching');
  },
  getProfile: () => {
    return api.get('/user/me');
  },
  getRecentlyAdded: () => {
    return api.get('/user/recently-added');
  },
  deleteWatchHistory: () => {
    return api.delete('/user/history');
  },
  deleteAccount: () => {
    return api.delete('/user/account');
  }
};

// authentication api endpoints
export const AuthAPI = {
  register: (userData) => {
    return api.post('/auth/register', userData);
  },
  login: (credentials) => {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    return api.post('/auth/login', formData);
  },
  getMe: () => {
    return api.get('/auth/me');
  },
  updateProfile: (userData) => {
    return api.put('/auth/me', userData);
  },
  getUserProfile: (userId) => {
    return api.get(`/auth/profile/${userId}`);
  },
  getUserByUsername: (username) => {
    return api.get(`/auth/profile/username/${username}`);
  }
};

// Watchlist API
export const WatchlistAPI = {
  getWatchlist: () => {
    return api.get('/watchlist');
  },
  addToWatchlist: (contentType, contentId) => {
    return api.post(`/watchlist/add?content_type=${contentType}&content_id=${contentId}`);
  },
  removeFromWatchlist: (contentType, contentId) => {
    return api.delete(`/watchlist/remove?content_type=${contentType}&content_id=${contentId}`);
  },
  checkWatchlist: (contentType, contentId) => {
    return api.get(`/watchlist/check?content_type=${contentType}&content_id=${contentId}`);
  },
  getUserWatchlist: (userId) => {
    return api.get(`/watchlist/user/${userId}`);
  }
};

// Comments API
export const CommentsAPI = {
  // Get comments for content (movie or show)
  getContentComments: (contentType, contentId, params = {}) => {
    return api.get(`/comments/content/${contentType}/${contentId}`, { params })
      .catch(error => {
        console.error(`Error fetching comments for ${contentType} ${contentId}:`, error);
        return { data: { success: false, data: { comments: [], total: 0 }, error: 'Failed to load comments' } };
      });
  },
  
  // Create a new comment
  createComment: async (commentData) => {
    try {
      return await api.post('/comments', commentData);
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  },
  
  // Like a comment - toggle behavior
  likeComment: async (commentId) => {
    try {
      return await api.post(`/comments/${commentId}/like`);
    } catch (error) {
      console.error(`Error liking comment ${commentId}:`, error);
      return { 
        data: { 
          success: false, 
          error: 'Failed to like comment. Please try again.' 
        } 
      };
    }
  },
  
  // Dislike a comment - toggle behavior
  dislikeComment: async (commentId) => {
    try {
      return await api.post(`/comments/${commentId}/dislike`);
    } catch (error) {
      console.error(`Error disliking comment ${commentId}:`, error);
      return { 
        data: { 
          success: false, 
          error: 'Failed to dislike comment. Please try again.' 
        } 
      };
    }
  },
  
  // Report a comment
  reportComment: async (commentId, reportData) => {
    try {
      return await api.post(`/comments/${commentId}/report`, reportData);
    } catch (error) {
      console.error(`Error reporting comment ${commentId}:`, error);
      return { 
        data: { 
          success: false, 
          error: 'Failed to report comment. Please try again.' 
        } 
      };
    }
  },
  
  // Get specific comment
  getComment: (commentId) => {
    return api.get(`/comments/${commentId}`)
      .catch(error => {
        console.error(`Error fetching comment ${commentId}:`, error);
        return { data: { success: false, data: null, error: 'Comment not found' } };
      });
  },
  
  // Get comment with all nested replies
  getCommentTree: (commentId) => {
    return api.get(`/comments/${commentId}/tree`)
      .catch(error => {
        console.error(`Error fetching comment tree for ${commentId}:`, error);
        return { data: { success: false, data: null, error: 'Failed to load comment thread' } };
      });
  },
  
  // Get replies for a comment
  getReplies: (commentId) => {
    return api.get(`/comments/${commentId}/tree`)
      .catch(error => {
        console.error(`Error fetching replies for comment ${commentId}:`, error);
        return { data: { success: false, data: { replies: [] }, error: 'Failed to load replies' } };
      });
  },
  
  // Fetch nested replies by their IDs
  fetchNestedReplies: async (replyIds) => {
    if (!replyIds || !replyIds.length) return [];
    
    try {
      // Create an array of promises for each reply ID
      const promises = replyIds.map(id => 
        api.get(`/comments/${id}`).catch(err => {
          console.error(`Error fetching reply ${id}:`, err);
          return { data: { success: false, data: null } };
        })
      );
      
      const responses = await Promise.all(promises);
      
      // Extract the data from each response
      return responses.map(response => {
        if (response.data && response.data.success) {
          return response.data.data;
        }
        return null;
      }).filter(Boolean); // Filter out any null values
    } catch (error) {
      console.error('Error fetching nested replies:', error);
      return [];
    }
  },
  
  // Update a comment with retry logic
  updateComment: async (commentId, content) => {
    // Add retry logic
    const maxRetries = 3;
    let retries = 0;
    
    while (retries < maxRetries) {
      try {
        return await api.put(`/comments/${commentId}`, { content });
      } catch (error) {
        retries++;
        if (retries >= maxRetries) {
          console.error(`Failed to update comment after ${maxRetries} attempts:`, error);
          throw new Error('Failed to update comment after multiple attempts. Please try again.');
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries - 1)));
      }
    }
  },
  
  // Delete a comment with retry logic
  deleteComment: async (commentId) => {
    // Add retry logic
    const maxRetries = 3;
    let retryCount = 0;
    
    const attemptDelete = async () => {
      try {
        return await api.delete(`/comments/${commentId}`);
      } catch (error) {
        console.error(`Error deleting comment ${commentId}:`, error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          // Exponential backoff: 0.5s, 1s, 2s
          const delay = Math.pow(2, retryCount) * 500;
          console.log(`Retrying comment deletion in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptDelete(); // Retry after delay
        }
        
        // If all retries fail, return a standardized error response
        return { 
          data: { 
            success: false, 
            error: 'Failed to delete comment. Please try again.' 
          } 
        };
      }
    };
    
    return attemptDelete();
  },
  
  // Get movie comments
  getMovieComments: (movieId, params = {}) => {
    return api.get(`/movies/${movieId}/comments`, { params })
      .catch(error => {
        console.error(`Error fetching comments for movie ${movieId}:`, error);
        return { data: { success: false, data: { comments: [], total: 0 }, error: 'Failed to load comments' } };
      });
  },
  
  // Get show comments
  getShowComments: (showId, params = {}) => {
    return api.get(`/shows/${showId}/comments`, { params })
      .catch(error => {
        console.error(`Error fetching comments for show ${showId}:`, error);
        return { data: { success: false, data: { comments: [], total: 0 }, error: 'Failed to load comments' } };
      });
  },
  
  // Get comments for a specific user
  getUserComments: (userId, params = {}) => {
    return api.get(`/comments/user/${userId}`, { params })
      .catch(error => {
        console.error(`Error fetching comments for user ${userId}:`, error);
        return { data: { success: false, data: { comments: [], total: 0 }, error: 'Failed to load comments' } };
      });
  },
  
  // Get comments for the current logged-in user
  getCurrentUserComments: (params = {}) => {
    return api.get('/user/me/comments', { params })
      .catch(error => {
        console.error('Error fetching current user comments:', error);
        return { data: { success: false, data: { comments: [], total: 0 }, error: 'Failed to load comments' } };
      });
  }
};

// Search API
export const SearchAPI = {
  // Get search suggestions
  getSuggestions: (query, limit = 5) => {
    return api.get(`/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`);
  },
  
  // Standard search
  search: async (query) => {
    try {
      const [moviesResponse, showsResponse] = await Promise.all([
        MoviesAPI.getAll({ search: query }),
        ShowsAPI.getAll({ search: query })
      ]);
      
      return {
        movies: moviesResponse.data.data || [],
        shows: showsResponse.data.data || []
      };
    } catch (error) {
      console.error('Search error:', error);
      return { movies: [], shows: [] };
    }
  }
};

// Replace existing searchContent function
export const searchContent = (query) => {
  return SearchAPI.search(query);
};

// Reports API
export const ReportsAPI = {
  // Create a new report
  createReport: (reportData) => {
    console.log("API: Creating report with data:", reportData);
    // Make sure content_type is valid
    if (reportData.content_type !== 'movie' && reportData.content_type !== 'show') {
      console.warn("Invalid content_type detected, forcing to 'show'");
      reportData.content_type = 'show';
    }
    return api.post('/reports/', reportData);
  },
  
  // Get user's reports
  getUserReports: (params = {}) => {
    return api.get('/reports/my-reports', { params });
  },
  
  // Get valid report reasons
  getReportReasons: () => {
    return api.get('/reports/reasons');
  }
};

// Export the API instance
export default api;